import { useDraggable } from '@dnd-kit/react';
import { ActionIcon, Avatar, Checkbox, HoverCard, Loader, Text, Tooltip } from '@mantine/core';
import {
  IconAlertCircle,
  IconCircleCheckFilled,
  IconHourglassHigh,
  IconPlayerPlayFilled,
  IconPhoto,
  IconStrikethrough,
} from '@tabler/icons-react';
import clsx from 'clsx';
import { useAtom, useAtomValue } from 'jotai';
import Link from 'next/link';
import { useCallback, useMemo, useState } from 'react';
import { selectedOpsIdsAtom } from '~/atoms/no-code-atoms';
import { completedItemsAtom } from '~/atoms/recorder-atoms';
import IconDraggable from '~/components/icons/IconDraggable';
import { useProjectId } from '~/hooks/context/useProjectId';
import type { OperationActionItemData } from '~/hooks/step/useHandleMoveOperation';
import { useLiveStateManager } from '~/hooks/useLiveStateManager';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import OperationInner from './OperationInner';
import OperationTabs from './OperationTabs';
import { TOUR_ID } from '~/components/tours/DraftEditorTour';
import { JOYRIDE_Z_INDEX } from '~/components/tours/common/MantineJoyride';
import { usePreviewFromLocation } from '~/hooks/vm/usePreviewFromLocation';
import { isOperationOptimistic } from '~/hooks/mutations/optimistic-handles';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import { useViewOperationImage } from './useViewOperationImage';

export default function Operation({
  operation,
  step,
  testContentId,
  readonly,
  disabled,
  className,
  showTabLabel = false,
  stepIdx,
  parentContexts,
  timeoutInfo,
}: {
  operation: SelectedOperation;
  step: SelectedStep;
  testContentId: string;
  readonly?: boolean;
  disabled?: boolean;
  className?: string;
  showTabLabel?: boolean;
  stepIdx: number;
  parentContexts?: OperationParentContext[];
  timeoutInfo?: { currentTimeoutMs: number; totalTimeoutMs?: number };
}) {
  const parentContext = parentContexts?.at(-1);

  const projectId = useProjectId();
  const [editing, setEditing] = useState(false);
  const { vmStatus, recorderState } = useLiveStateManager();
  const completedItems = useAtomValue(completedItemsAtom);
  const { openOperationImage } = useViewOperationImage();

  const isCompletedOp = completedItems.completedOperationIds.includes(operation.id);

  const vmStatusOpMatch =
    vmStatus?.executionState?.operationId === operation.id ||
    vmStatus?.executionState?.stepSourceOperationId === operation.id;

  // Only show replaying when recorder/previewer open
  const operationReplaying = !!recorderState && vmStatus?.state === 'replaying' && vmStatusOpMatch;

  const isOptimisticOperation = isOperationOptimistic(operation.id);

  // We must disable dragging if the operation is being edited. Otherwise dnd kit will treat 'enter' as starting to drag the element
  const dragAndSelectDisabled =
    isOptimisticOperation ||
    readonly ||
    disabled ||
    editing ||
    // We also want to enable dragging when the execution type is record
    (vmStatus?.state !== 'closed' && vmStatus?.state !== undefined && recorderState?.executionType !== 'record');

  const { isDragging, handleRef, ref } = useDraggable({
    id: operation.id,
    data: {
      type: 'operation',
      operationId: operation.id,
      sourceParentContext: parentContext,
      sourceStepId: step.id,
    } satisfies OperationActionItemData,
    disabled: dragAndSelectDisabled,
  });

  const isActiveRecordingPosition = recorderState?.operation?.id === operation.id && recorderState?.stepId === step.id;

  const cursorAllowed = step.mode !== 'AI_DYNAMIC' && !readonly;

  const isJoyrideOperation = operation.id === TOUR_ID.PREVIEW_OP_BUTTON;

  // Basically if the recorder is not on OR if the recorder cursor is behind the current operation
  const isBeforeCursor =
    !recorderState ||
    (recorderState?.stepOrder !== undefined &&
      (recorderState?.stepOrder < step.stepOrder ||
        (recorderState?.stepOrder === step.stepOrder && (recorderState?.operation?.order ?? 0) <= operation.order)));

  const previewCursorAllowed = cursorAllowed && !isActiveRecordingPosition && isBeforeCursor && !isOptimisticOperation;

  const previewFromLocation = usePreviewFromLocation({ testContentId });

  const [selectedOpsIds, setSelectedOps] = useAtom(selectedOpsIdsAtom);
  const isOpSelected = useMemo(() => selectedOpsIds.includes(operation.id), [selectedOpsIds, operation.id]);
  const toggleSelectOp = useCallback(
    () =>
      setSelectedOps((prev) => (isOpSelected ? prev.filter((opId) => opId !== operation.id) : [...prev, operation.id])),
    [isOpSelected, operation, setSelectedOps],
  );

  // Check if any hover card content (preview button or screenshot button) should be shown
  const showPreviewButton = !!previewCursorAllowed || isJoyrideOperation;
  const showScreenshotButton = !!(
    operation.beforeScreenshot ||
    operation.actionScreenshot ||
    operation.afterScreenshot
  );
  const hasHoverCardContent = showPreviewButton || showScreenshotButton;

  return (
    <div className="w-full">
      {/* Timeout indicator */}
      {!!timeoutInfo && (
        <div className="mb-1 flex flex-col gap-1 pl-10 text-[var(--mantine-color-dimmed)]">
          <div className="flex items-center gap-1">
            <IconAlertCircle size={14} />
            <Text size="sm">Waiting for locator...</Text>
            <Text size="sm">({Math.ceil(timeoutInfo.currentTimeoutMs / 1000)}s)</Text>
          </div>

          {/* Waiting for locator progress bar */}
          {/* Mantine's Progress component doesn't work because it can't catch up with the progress updates due to fast state changes, so it just freezes */}
          <div className="h-1 w-full flex-grow rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-1 rounded-full bg-gray-400 dark:bg-gray-500"
              style={{
                width: `${timeoutInfo?.totalTimeoutMs ? (timeoutInfo.currentTimeoutMs / timeoutInfo.totalTimeoutMs) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Hover card for quick actions */}
      <HoverCard
        position="right-start"
        styles={{ dropdown: { padding: 0, border: 'none' } }}
        // disable the hover card when dragging, if the operation has a parent, or if there's no content to show
        disabled={isDragging || parentContext !== undefined || !hasHoverCardContent}
        offset={3}
        openDelay={100}
        closeDelay={100}
        // doing +1 didn't seem to work, so we are using *2 instead
        zIndex={isJoyrideOperation ? JOYRIDE_Z_INDEX * 2 : 1500}
        initiallyOpened={isJoyrideOperation}
        withinPortal
      >
        <HoverCard.Target>
          <div
            className={clsx('group relative', className, {
              'relative z-[9999]': isDragging,
            })}
            ref={ref}
          >
            <div>
              <div className="ml-4">
                <OperationTabs
                  isForDisplayingOpensPageAlias={false}
                  operation={operation}
                  testContentId={testContentId}
                  readonly={readonly}
                  showTabLabel={showTabLabel}
                />
              </div>
              <div
                className={clsx('flex w-full flex-row items-center', {
                  'opacity-50': isOptimisticOperation,
                })}
              >
                <div className="flex flex-shrink-0 items-center gap-1 pr-[2px]">
                  {/* Don't show the checkbox if the operation has a parent (we don't handle selecting child operations in conditionals/components yet) */}
                  {!parentContext && (
                    <Checkbox
                      size="xs"
                      checked={isOpSelected}
                      disabled={dragAndSelectDisabled}
                      onChange={toggleSelectOp}
                      className={clsx(
                        'drop-shadow-[0_0_1.5px_rgba(0,0,0,0.2)] transition-opacity delay-200 duration-200',
                        {
                          'opacity-0': !isOpSelected,
                          'group-hover:opacity-100': !dragAndSelectDisabled && !isOpSelected && !isDragging,
                        },
                      )}
                    />
                  )}

                  {isCompletedOp ?
                    <div className="w-4">
                      <IconCircleCheckFilled
                        title="Operation Success"
                        size={14}
                        className="text-green-600"
                      />
                    </div>
                  : operationReplaying || isOptimisticOperation ?
                    <div className="w-4 px-1">
                      <div className="flex flex-col">
                        <Loader
                          className="justify-center self-center"
                          color="blue"
                          size={12}
                        />
                      </div>
                    </div>
                  : <div
                      className={clsx(
                        'flex-shrink-0 cursor-grab opacity-0 transition-opacity delay-200 duration-200 group-hover:opacity-100',
                        {
                          invisible: dragAndSelectDisabled,
                        },
                      )}
                      ref={handleRef}
                    >
                      <IconDraggable className="h-4 w-4 text-gray-400" />
                    </div>
                  }
                </div>

                <div className="flex w-full min-w-0 flex-row items-center gap-2">
                  {operation.skip ?
                    <Tooltip label="Operation will be skipped">
                      <Avatar
                        size="sm"
                        radius="xl"
                      >
                        <IconStrikethrough size={16} />
                      </Avatar>
                    </Tooltip>
                  : null}
                  <div className="w-full min-w-0 text-sm">
                    <div
                      className={`rounded-md transition-all duration-200 ${isDragging ? 'border-2 border-blue-400 bg-blue-50 shadow-lg dark:border-blue-300 dark:bg-blue-950' : 'border-transparent'}`}
                    >
                      <OperationInner
                        operation={operation}
                        previewCursorAllowed={previewCursorAllowed}
                        step={step}
                        testContentId={testContentId}
                        readonly={readonly}
                        disabled={disabled}
                        editing={editing}
                        setEditing={setEditing}
                        disableMenu={false}
                        stepIdx={stepIdx}
                        parentContexts={parentContexts}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {!!operationReplaying && operation.type === 'RECEIVE_EMAIL' && (
                <div className="flex items-center gap-1 pl-7 text-sm text-gray-500">
                  <IconHourglassHigh size={14} />
                  <div>
                    Checking emails
                    <span className="animate-blink">...</span>
                    <Link
                      href={`/project/${projectId}/mail`}
                      target="_blank"
                      className="ml-2 text-blue-500 hover:underline"
                    >
                      View inbox
                    </Link>
                  </div>
                </div>
              )}
              <div className="ml-4">
                <OperationTabs
                  isForDisplayingOpensPageAlias={true}
                  operation={operation}
                  testContentId={testContentId}
                  readonly={readonly}
                  showTabLabel={showTabLabel}
                />
              </div>
            </div>
          </div>
        </HoverCard.Target>
        <HoverCard.Dropdown className={isJoyrideOperation ? 'pointer-events-none' : ''}>
          <div className="flex w-full flex-col items-center justify-center gap-1 rounded-md border border-gray-300 dark:border-stone-700">
            {!!showPreviewButton && (
              <Tooltip
                label="Preview to this action"
                multiline
                withArrow
                className="text-center"
                openDelay={500}
                position="right"
              >
                <ActionIcon
                  size="md"
                  variant="subtle"
                  color="green"
                  onClick={() =>
                    previewFromLocation({
                      step,
                      operation: { id: operation.id, order: operation.order },
                    })
                  }
                >
                  <IconPlayerPlayFilled
                    size={16}
                    className="text-green-500"
                  />
                </ActionIcon>
              </Tooltip>
            )}
            {!!showScreenshotButton && (
              <Tooltip
                label="View screenshot"
                multiline
                withArrow
                className="text-center"
                openDelay={500}
                position="right"
              >
                <ActionIcon
                  size="md"
                  variant="subtle"
                  color="gray"
                  onClick={() => openOperationImage(operation.id)}
                >
                  <IconPhoto
                    size={16}
                    className="text-[var(--mantine-color-text)]"
                  />
                </ActionIcon>
              </Tooltip>
            )}
          </div>
        </HoverCard.Dropdown>
      </HoverCard>
    </div>
  );
}
